/**
 * The payroll.
 *
 * Deliberately a separate panel from the retainers one, because a hireling is a
 * different arrangement rather than a cheaper henchman: paid a wage, outside the
 * Charisma cap, no share of treasure, and no character sheet. Showing the two in
 * one list would suggest a spectrum the rules do not have.
 *
 * **The bill is shown as a range and never as a single number** — one printed
 * row is 50-150 gp a week — and the book's own qualification is printed beneath
 * it, because a total that looked like the cost of employing somebody would
 * understate it by room, board, food and travel.
 */

import { useEffect, useState } from 'react';

import { api, ApiError } from '../api.js';

interface Wage {
  low: number;
  high: number;
  printed: string;
}

interface Occupation {
  sort: 'common' | 'military';
  occupation: string;
  weekly?: Wage;
  monthly: Wage;
  loadout?: string;
  profession: string;
}

interface Employed {
  id: string;
  name: string;
  occupation: string;
  sort: 'common' | 'military';
  alignment: string;
  wage: { monthly: Wage; weekly?: Wage; loadout?: string };
  /** What is charged from the employer's purse; absent for one hired before wages were. */
  agreed?: { text: string; period: 'week' | 'month' };
  morale: { base: number; total: number };
}

interface Upkeep {
  low: number;
  high: number;
  period: string;
  periods: number;
  excludes: string;
  lines: { occupation: string; count: number; low: number; high: number }[];
}

/** `footmanHeavy` → `footman heavy`. The keys are camelCase table keys. */
function label(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').toLowerCase();
}

/**
 * The wage a hireling is actually paid, as the book prints it: a trade on its
 * weekly column, a soldier on the monthly one (the only column the military
 * table has). Never one derived from the other.
 */
function paidWageLabel(kind: { weekly?: Wage; monthly: Wage }): string {
  return kind.weekly ? wageLabel(kind.weekly, 'week') : wageLabel(kind.monthly, 'month');
}

/** A wage as the book prints it, so it can be checked against the page. */
function wageLabel(wage: Wage, per: string): string {
  return `${wage.printed} gp/${per}`;
}

export function HirelingsPanel({
  campaignId,
  myCharacterId,
}: {
  readonly campaignId: string;
  readonly myCharacterId?: string;
}) {
  const [employed, setEmployed] = useState<Employed[]>([]);
  const [upkeep, setUpkeep] = useState<Upkeep | null>(null);
  const [offer, setOffer] = useState<Occupation[]>([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const body = await api.get<{ hirelings: Employed[]; upkeep: Upkeep }>(
          `/api/campaigns/${campaignId}/hirelings`,
        );
        if (!live) return;
        setEmployed(body.hirelings);
        setUpkeep(body.upkeep);
      } catch {
        // A table with no payroll is the common case; nothing to say.
      }
    })();
    return () => {
      live = false;
    };
  }, [campaignId, version]);

  useEffect(() => {
    // ⚠ Hirelings are the party's, not a role's: anybody may look at who is
    // available and what they cost. The gate predated the role's removal.
    if (!open || offer.length > 0) return;
    let live = true;
    void (async () => {
      try {
        const body = await api.get<{ occupations: Occupation[] }>(
          `/api/campaigns/${campaignId}/hirelings/offer`,
        );
        if (live) setOffer(body.occupations);
      } catch (problem) {
        if (live) setError(problem instanceof ApiError ? problem.message : 'no rates available');
      }
    })();
    return () => {
      live = false;
    };
  }, [campaignId, open, offer.length]);

  const hire = async () => {
    const kind = offer.find((o) => o.occupation === chosen);
    if (!kind || !myCharacterId) return;
    setError(null);
    try {
      await api.post(`/api/campaigns/${campaignId}/hirelings`, {
        employerCharacterId: myCharacterId,
        sort: kind.sort,
        occupation: kind.occupation,
        name: name.trim(),
      });
      setName('');
      setChosen('');
      setOpen(false);
      setVersion((n) => n + 1);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'that hire was refused');
    }
  };

  const letGo = async (id: string) => {
    setError(null);
    try {
      await api.delete(`/api/campaigns/${campaignId}/hirelings/${id}`);
      setVersion((n) => n + 1);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'that did not work');
    }
  };

  // ⚠ **This used to read `employed.length === 0` ANDed with the negation of
  // a flag for the now-removed role** — a table with nobody on the books yet
  // still showed that role's holder the "Take somebody on" button, because the
  // negated flag made the whole condition false for them. Removing the role
  // dropped only the negated half instead of the gate itself, which flipped
  // "only that role could hire the first one" into "nobody can ever hire the
  // first one, at all, on any table" — the hire button lived inside this same
  // early return, so it was unreachable exactly when it was needed. Hirelings
  // are the party's, per this file's own opening comment; the panel now
  // always renders.

  return (
    <section className="space-y-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Payroll</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        Hirelings are paid help: porters, guards, specialists. Unlike henchmen, who are loyal followers and limited by
        your Charisma, there is <strong>no limit</strong> on how many you can take on — the Charisma cap
        does not affect hirelings. What holds you back is paying them: each has a wage, taken from your purse
        when it falls due.
      </p>

      {employed.length === 0 && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">Nobody on the books.</p>
      )}

      <ul className="space-y-1 text-sm">
        {employed.map((one) => (
          <li key={one.id} className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium">{one.name}</span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {label(one.occupation)} ·{' '}
              {one.agreed
                ? `paid ${one.agreed.text} a ${one.agreed.period}`
                : paidWageLabel(one.wage)}{' '}
              · morale {one.morale.total}
            </span>
            {one.wage.loadout && (
              <span className="text-xs text-neutral-400" title={one.wage.loadout}>
                (equipped)
              </span>
            )}
            {/* ⚠ Hirelings are the party's — anybody may take somebody on or
                let them go. The gate was the role's, and the role is gone. */}
            {(
              <button
                type="button"
                onClick={() => void letGo(one.id)}
                className="text-xs text-neutral-500 dark:text-neutral-400 underline"
              >
                let go
              </button>
            )}
          </li>
        ))}
      </ul>

      {upkeep && upkeep.lines.length > 0 && (
        <div className="rounded-md bg-neutral-50 p-2 text-xs dark:bg-neutral-900">
          <p>
            {/* A range, never a midpoint: one printed row genuinely is a range,
                and averaging it would invent a figure. */}
            <strong>
              {upkeep.low === upkeep.high
                ? `${upkeep.low.toLocaleString()} gp`
                : `${upkeep.low.toLocaleString()}–${upkeep.high.toLocaleString()} gp`}
            </strong>{' '}
            per {upkeep.period === 'weekly' ? 'week' : 'month'} in wages
          </p>
          <p className="mt-1 text-neutral-500 dark:text-neutral-400">{upkeep.excludes}</p>
        </div>
      )}

      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md border border-neutral-300 px-3 py-1 text-sm dark:border-neutral-700"
        >
          Take somebody on
        </button>
      )}

      {open && (
        <div className="space-y-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
          <select
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          >
            <option value="">Choose a trade…</option>
            {/* Grouped, because the two tables are two different things: a
                trade is hired in a town, a soldier goes into danger. */}
            <optgroup label="Trades">
              {offer
                .filter((o) => o.sort === 'common')
                .map((o) => (
                  <option key={o.occupation} value={o.occupation}>
                    {label(o.occupation)} — {paidWageLabel(o)}
                  </option>
                ))}
            </optgroup>
            <optgroup label="Soldiers">
              {offer
                .filter((o) => o.sort === 'military')
                .map((o) => (
                  <option key={o.occupation} value={o.occupation}>
                    {label(o.occupation)} — {paidWageLabel(o)}
                  </option>
                ))}
            </optgroup>
          </select>
          {chosen && offer.find((o) => o.occupation === chosen)?.loadout && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Brings: {offer.find((o) => o.occupation === chosen)?.loadout}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={name.trim() === '' || chosen === '' || !myCharacterId}
              onClick={() => void hire()}
              className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-neutral-700"
            >
              Hire
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              className="rounded-md px-3 py-1 text-sm text-neutral-500 dark:text-neutral-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>}
    </section>
  );
}
